import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import VirtualTryOnService from '../../services/virtualTryOnService';
import { trackTryOn } from '../../utils/analytics';

const VirtualTryOnModal = ({ isOpen, onClose, product, onTryOnComplete }) => {
  // Opened vs started vs finished are three different drop-off points: people
  // who never upload a photo are a different problem from people whose render
  // fails, and only separate events can tell them apart.
  useEffect(() => {
    if (isOpen) trackTryOn('opened', product?._id);
  }, [isOpen, product?._id]);

  const [step, setStep] = useState('upload'); // upload, preview, processing, result
  const [userPhoto, setUserPhoto] = useState(null);
  const [userPhotoUrl, setUserPhotoUrl] = useState('');
  const [tryOnResult, setTryOnResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState('');
  const [error, setError] = useState('');
  const [errorDebugInfo, setErrorDebugInfo] = useState(null);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // AbortController ref for cancelling polling
  const abortControllerRef = useRef(null);
  // Track mount state to avoid state updates after unmount
  const isMountedRef = useRef(true);

  // Camera and photo capture states
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState('');

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetModal();
    }
  }, [isOpen]);

  // Connect camera stream to video element when camera UI is shown
  useEffect(() => {
    if (showCamera && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [showCamera]);

  const resetModal = () => {
    setStep('upload');
    setUserPhoto(null);
    setUserPhotoUrl('');
    setTryOnResult(null);
    setProcessing(false);
    setProgress(0);
    setProgressStage('');
    setError('');
    setErrorDebugInfo(null);
    setShowCamera(false);
    setCameraError('');
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  // Handle file upload — accepts ALL image formats (backend converts via sharp)
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 20MB limit — backend sharp will compress to optimal size
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image size must be less than 20MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!isMountedRef.current) return;
      setUserPhoto(file);
      setUserPhotoUrl(e.target.result);
      setStep('preview');
    };

    reader.onerror = () => {
      toast.error('Failed to read image file');
    };

    reader.readAsDataURL(file);
  };

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      streamRef.current = stream;
      setShowCamera(true);
      setCameraError('');

    } catch (err) {
      setCameraError('Unable to access camera. Please upload a photo instead.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob || !isMountedRef.current) return;

      // FIX #18: Check captured blob size
      if (blob.size > 4 * 1024 * 1024) {
        toast.error('Captured photo is too large. Please try from a closer distance.');
        return;
      }

      const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
      setUserPhoto(file);
      setUserPhotoUrl(canvas.toDataURL('image/jpeg'));
      setStep('preview');
      stopCamera();
    }, 'image/jpeg', 0.85);
  };

  // Submit job + poll for result (async polling — no 504 timeout)
  const processVirtualTryOn = useCallback(async () => {
    if (processing) return;
    if (!userPhoto || !product) return;

    setProcessing(true);
    setStep('processing');
    setProgress(0);
    setProgressStage('Uploading your photo...');
    setError('');
    trackTryOn('started', product?._id);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const validation = VirtualTryOnService.validateImageFile(userPhoto);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const optimizedPhoto = await VirtualTryOnService.resizeImageIfNeeded(userPhoto);

      // onProgress callback — receives REAL progress from server
      const onProgress = (serverProgress, stage) => {
        if (!isMountedRef.current) return;
        setProgress(serverProgress);
        setProgressStage(stage || 'Processing...');
      };

      const result = await VirtualTryOnService.processTryOn(
        optimizedPhoto,
        product._id,
        product.images?.[0] || '',
        product.category,
        controller.signal,
        onProgress
      );

      if (!isMountedRef.current) return;

      if (result.success) {
        setTryOnResult(result.data);
        setProgress(100);
        setProgressStage('Done!');
        setStep('result');
        trackTryOn('success', product?._id);
        onTryOnComplete?.(result.data);
      } else {
        throw new Error(result.message || 'Try-on processing failed');
      }

    } catch (err) {
      if (!isMountedRef.current) return;

      if (err.name === 'AbortError') {
        console.log('Virtual try-on request was cancelled');
        return;
      }

      setError(err.message || `Try-on failed (${err.name || 'unknown error'}). Check diagnostic info below.`);
      setErrorDebugInfo(err.debugInfo || null);
      setStep('error');
      trackTryOn('failed', product?._id);
    } finally {
      if (isMountedRef.current) {
        setProcessing(false);
      }
      abortControllerRef.current = null;
    }
  }, [processing, userPhoto, product, onTryOnComplete]);

  // Retry try-on
  const retryTryOn = () => {
    setStep('upload');
    setError('');
    setProgress(0);
  };

  // FIX #13: Download via fetch + blob URL to handle cross-origin Cloudinary images
  const downloadResult = async () => {
    if (!tryOnResult?.imageUrl) return;

    try {
      const response = await fetch(tryOnResult.imageUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `tryon-${product?.name || 'result'}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup blob URL after a short delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (downloadErr) {
      // Fallback: open in new tab if fetch fails
      console.error('Download failed, opening in new tab:', downloadErr);
      window.open(tryOnResult.imageUrl, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Virtual Try-On</h2>
              <p className="text-gray-600 mt-1">
                See how {product?.name} looks on you!
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center transition-colors border border-gray-200"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-r from-orange-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload Your Photo</h3>
                <p className="text-gray-600 mb-6">
                  Take a photo or upload an image to see how this product looks on you
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Camera Option */}
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-orange-400 transition-colors cursor-pointer">
                  <button
                    onClick={startCamera}
                    className="w-full h-full flex flex-col items-center justify-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Take Photo</h4>
                    <p className="text-gray-600 text-sm">Use your camera to take a new photo</p>
                  </button>
                </div>

                {/* Upload Option */}
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-orange-400 transition-colors cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-full flex flex-col items-center justify-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-green-200 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Photo</h4>
                    <p className="text-gray-600 text-sm">Choose an existing photo from your device</p>
                    <span className="inline-block mt-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700">
                      All image formats supported
                    </span>
                  </button>
                </div>
              </div>

              {/* Camera Interface */}
              {showCamera && (
                <div className="fixed inset-0 z-60 bg-black flex items-center justify-center p-4">
                  <div className="bg-white rounded-3xl p-6 max-w-2xl w-full">
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">Take Your Photo</h3>
                      <p className="text-gray-600">Position yourself in the frame and click capture</p>
                    </div>

                    <div className="relative mb-6">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-full rounded-2xl"
                      />
                      <canvas ref={canvasRef} className="hidden" />
                    </div>

                    {cameraError && (
                      <div className="text-red-600 text-center mb-4">{cameraError}</div>
                    )}

                    <div className="flex justify-center space-x-4">
                      <button
                        onClick={stopCamera}
                        className="px-6 py-3 bg-gray-500 text-white rounded-xl font-semibold hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={capturePhoto}
                        className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-pink-600 transition-all duration-300"
                      >
                        Capture Photo
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Requirements — FIX #19: Match backend limits */}
              <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Photo Tips:</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• All image formats supported (JPG, PNG, HEIC, WEBP, etc.)</li>
                  <li>• Maximum file size: 20MB</li>
                  <li>• Full body shot recommended for best results</li>
                  <li>• Well-lit photo with clear visibility</li>
                </ul>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Review Your Photo</h3>
                <p className="text-gray-600">Make sure your photo is clear and shows your full body</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Photo */}
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-3">Your Photo</h4>
                  <div className="relative">
                    <img
                      src={userPhotoUrl}
                      alt="Your photo"
                      className="w-full h-80 object-cover object-top rounded-2xl border-2 border-gray-200"
                    />
                    <button
                      onClick={() => setStep('upload')}
                      className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Product — FIX #21: Handle missing product images */}
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-3">Product</h4>
                  <div className="relative">
                    {product?.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product?.name}
                        className="w-full h-80 object-cover object-top rounded-2xl border-2 border-gray-200"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-80 rounded-2xl border-2 border-gray-200 bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No product image</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{product?.name}</p>
                </div>
              </div>

              <div className="text-center">
                {/* FIX #10: Disable button during processing to prevent double-click */}
                <button
                  onClick={processVirtualTryOn}
                  disabled={processing}
                  className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:from-orange-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Processing...' : 'Start Virtual Try-On'}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Processing typically takes 15-30 seconds
                </p>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center space-y-6">
              <div className="w-24 h-24 bg-gradient-to-r from-orange-100 to-pink-100 rounded-full flex items-center justify-center mx-auto">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-500"></div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Processing Your Try-On</h3>
                <p className="text-gray-600 mb-2">
                  Our AI is creating a realistic image of you wearing this product
                </p>

                {/* Real server stage */}
                {progressStage && (
                  <p className="text-orange-600 font-medium text-sm mb-4">{progressStage}</p>
                )}

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-pink-500 h-3 rounded-full transition-all duration-1000"
                    style={{ width: `${Math.round(progress)}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600">{Math.round(progress)}% complete</p>
              </div>

              <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200 max-w-md mx-auto">
                <p className="text-blue-800 text-sm">
                  This typically takes 30-60 seconds. You can keep this window open — your result will appear automatically.
                </p>
              </div>
            </div>
          )}

          {step === 'result' && tryOnResult && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Your Virtual Try-On Result</h3>
                <p className="text-gray-600">See how {product?.name} looks on you!</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Original Photo */}
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-3">Original Photo</h4>
                  <img
                    src={userPhotoUrl}
                    alt="Original photo"
                    className="w-full h-80 object-cover object-top rounded-2xl border-2 border-gray-200"
                  />
                </div>

                {/* Try-On Result */}
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-3">Try-On Result</h4>
                  {tryOnResult.imageUrl ? (
                    <img
                      src={tryOnResult.imageUrl}
                      alt="Virtual try-on result"
                      className="w-full h-80 object-cover object-top rounded-2xl border-2 border-orange-200 shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="w-full h-80 rounded-2xl border-2 border-red-200 bg-red-50 items-center justify-center" style={{ display: tryOnResult.imageUrl ? 'none' : 'flex' }}>
                    <div className="text-center p-4">
                      <p className="text-red-600 font-semibold mb-2">Image failed to load</p>
                      <p className="text-red-500 text-xs break-all">URL: {tryOnResult.imageUrl || 'No URL returned'}</p>
                      {tryOnResult.debugInfo?.lastStep && (
                        <p className="text-gray-500 text-xs mt-2">Last step: {tryOnResult.debugInfo.lastStep}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={downloadResult}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-300"
                >
                  Download Result
                </button>
                <button
                  onClick={retryTryOn}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-300"
                >
                  Try Another Photo
                </button>
              </div>

              <div className="bg-green-50 rounded-2xl p-4 border border-green-200 text-center">
                <h4 className="font-semibold text-green-900 mb-2">How does it look?</h4>
                <p className="text-green-800 text-sm">
                  Love the fit? Add this item to your cart and make it yours!
                </p>
              </div>
            </div>
          )}

          {/* FIX #14: Dedicated error step so error is always clearly visible */}
          {step === 'error' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>

              <div className="bg-red-50 rounded-2xl p-6 border border-red-200 text-center max-w-lg mx-auto">
                <h4 className="font-semibold text-red-900 mb-2 text-lg">Virtual Try-On Failed</h4>
                <p className="text-red-800 text-sm mb-4">{error}</p>

                {/* Show exact failure point from server debug info */}
                {errorDebugInfo && (
                  <div className="bg-gray-900 rounded-xl p-4 mb-4 text-left overflow-x-auto">
                    <p className="text-green-400 text-xs font-mono mb-2">Diagnostic Info (for debugging):</p>
                    <p className="text-yellow-300 text-xs font-mono mb-1">
                      Failed at: <span className="text-white font-bold">{errorDebugInfo.lastStep || 'unknown'}</span>
                    </p>
                    {errorDebugInfo.steps && errorDebugInfo.steps.length > 0 && (
                      <div className="mt-2 border-t border-gray-700 pt-2">
                        <p className="text-green-400 text-xs font-mono mb-1">Step log:</p>
                        {errorDebugInfo.steps.slice(-5).map((s, i) => (
                          <p key={i} className="text-gray-300 text-xs font-mono truncate">
                            [{s.step}] {JSON.stringify(Object.fromEntries(Object.entries(s).filter(([k]) => k !== 'step' && k !== 'time'))).substring(0, 120)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-center space-x-3">
                  <button
                    onClick={retryTryOn}
                    className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-pink-600 transition-all duration-300"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={onClose}
                    className="bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200 max-w-lg mx-auto">
                <h4 className="font-semibold text-amber-900 mb-2">Tips for better results:</h4>
                <ul className="text-amber-800 text-sm space-y-1">
                  <li>• Any image format works — iPhone photos (HEIC) are fine</li>
                  <li>• Use a well-lit, clear photo (no dark/blurry images)</li>
                  <li>• Show your full body in the frame</li>
                  <li>• Use a plain background if possible</li>
                  <li>• Keep file size under 20MB</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualTryOnModal;
